import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { Auth } from '@freelancer/auth';
import { Pwa } from '@freelancer/pwa';
import type { Observable } from 'rxjs';
import {
  LanguageSwitcherLinkDisplay,
  LanguageSwitcherTheme,
} from './language-switcher.types';

@Component({
  standalone: false,
  selector: 'fl-language-switcher',
  template: `
    @if (!(isLoggedIn$ | flAsync)) {
      <fl-language-switcher-link
        [color]="color"
        [display]="linkDisplay"
        [isLoggedIn]="false"
        [useLanguageSwitcherModal]="useLanguageSwitcherModal"
        (languageSwitcherClick)="languageSwitcherClick.emit()"
      />
    } @else {
      @if (!forceInlineSwitcher) {
        <fl-language-switcher-link
          class="mobile:hide"
          [color]="color"
          [display]="linkDisplay"
          [isLoggedIn]="true"
        ></fl-language-switcher-link>
      }
      <!-- this is not an "*ngIf" because we can show either when they are not forced -->
      <fl-language-switcher-inline
        [compact]="compact"
        [class.desktop:hide]="!forceInlineSwitcher"
        [class.tablet:hide]="!forceInlineSwitcher"
      ></fl-language-switcher-inline>
    }
  `,
  styleUrls: ['./language-switcher.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LanguageSwitcherComponent implements OnInit {
  private auth = inject(Auth);
  private pwa = inject(Pwa);

  /**
   * Whether or not to attach a label to the inline switcher
   **/
  @Input() compact = false;

  /**
   * Theme for the link-based switcher.
   */
  @Input() color: LanguageSwitcherTheme;

  /**
   * Display format for the link-based switcher.
   */
  @Input() linkDisplay: LanguageSwitcherLinkDisplay =
    LanguageSwitcherLinkDisplay.FULL;

  /**
   * Opens the language switcher modal instead of navigating to the page.
   */
  @Input() useLanguageSwitcherModal = false;

  @Output() languageSwitcherClick = new EventEmitter<void>();

  /**
   * Whether or not the switcher is disabled entirely.
   */
  disabled: Promise<boolean>;

  /**
   * Whether or not to force the inline language switcher.
   * Toggling this off will enable the link-based switcher on desktop
   **/
  forceInlineSwitcher: boolean;

  /**
   * Whether or not the user is logged in.
   * Display link version if logged out.
   */
  isLoggedIn$: Observable<boolean>;

  ngOnInit(): void {
    this.isLoggedIn$ = this.auth.isLoggedIn();

    this.forceInlineSwitcher =
      // installed mode always sees the inline switcher
      this.pwa.isInstalled();
  }
}
